import deepEqual from 'fast-deep-equal'
import {useEffect} from 'react'

import {fetchProjectWithAllParentsData} from '../actions/projects'
import type {Node} from '../components/common/Tree/Tree.types'
import {getBuildTypesHash, getProjectsHash, getProjectsReady} from '../selectors'
import {getProjectsData} from '../selectors/projectTrees'
import type {ActiveEntityProps, BuildTypeId, ProjectId} from '../types'
import {ROOT_PROJECT_ID} from '../types'
import {notNull} from '../utils/guards'
import {combineRefetchables} from '../utils/refetchable'

import {useAppDispatch, useAppSelector} from 'src/hooks/react-redux'

type NodeWithReadyState = Node & {
  needsFetch?: ProjectId
}

export default function useProjectHierarchy(
  buildTypeIds: BuildTypeId[],
  mapper: (props: ActiveEntityProps) => Node,
) {
  const projectsReady = useAppSelector(getProjectsReady)
  const tree = useAppSelector(state => {
    const result: NodeWithReadyState[] = []
    if (!projectsReady) {
      return result
    }
    const projects = getProjectsData(state)
    const projectsHash = getProjectsHash(state)
    const buildTypesHash = getBuildTypesHash(state)

    const hash: Record<ProjectId, Node> = {}
    function getOrCreateProjectNode(projectId: ProjectId) {
      if (hash[projectId] != null) {
        return hash[projectId]
      }
      const node: NodeWithReadyState = (hash[projectId] = mapper({projectId}))
      const project = projectsHash[projectId]
      if (project == null) {
        node.needsFetch = projectId
      }
      const parentId = project?.parentProjectId
      if (parentId != null && parentId !== ROOT_PROJECT_ID) {
        const parent = getOrCreateProjectNode(parentId)
        parent.children ??= []
        parent.children.push(node)
      } else {
        result.push(node)
      }
      return node
    }
    for (const buildTypeId of buildTypeIds) {
      const node = mapper({buildTypeId})
      const projectId = buildTypesHash[buildTypeId]?.projectId
      if (projectId != null) {
        const parent = getOrCreateProjectNode(projectId)
        parent.children ??= []
        parent.children.push(node)
      }
    }
    Object.values(hash).forEach(node =>
      node.children?.sort((a, b) => {
        const [aType, aId] = a.value.split(/:(.+)/)
        const [bType, bId] = b.value.split(/:(.+)/)
        if (aType === 'project') {
          return bType === 'project' ? projects.indexOf(aId) - projects.indexOf(bId) : -1
        }
        if (bType === 'project') {
          return 1
        }
        const projectId = buildTypesHash[aId]?.projectId
        const buildTypes = projectId != null ? projectsHash[projectId]?.buildTypes?.buildType : null
        return buildTypes != null ? buildTypes.indexOf(aId) - buildTypes.indexOf(bId) : 0
      }),
    )
    return result
  }, deepEqual)
  const ready = projectsReady && tree.every(rootNode => !rootNode.needsFetch)
  const dispatch = useAppDispatch()
  useEffect(
    () =>
      combineRefetchables(
        tree
          .map(rootNode =>
            rootNode.needsFetch
              ? dispatch(fetchProjectWithAllParentsData(rootNode.needsFetch))
              : null,
          )
          .filter(notNull),
      ).unsubscribe,
    [dispatch, tree],
  )

  return {tree, ready}
}
